"use client";

import type { Spec } from "@json-render/core";
import {
  ActionProvider,
  Renderer,
  StateProvider,
  VisibilityProvider,
} from "@json-render/react";
import { memo, useMemo } from "react";
import { normalizeVizTreeChildren } from "@/lib/ai/visualization/runtime/stream-patches";
import type { VizDatasetEnvelope } from "@/lib/ai/visualization/types";
import { vizComponentRegistry } from "./registry";

type Props = {
  tree: Spec | null;
  data: VizDatasetEnvelope | null;
  isStreaming?: boolean;
};

const EMPTY_ACTION_HANDLERS = {} as const;

export const VizRenderer = memo(function VizRenderer({
  tree,
  data,
  isStreaming,
}: Props) {
  // Remount StateProvider when the dataset changes.
  // Avoid JSON.stringify(data) here: data can be large and re-render often.
  const dataKey = useMemo(() => {
    if (!data) return "viz-data-empty";
    const datasetIds = Object.keys(data.viz.datasets ?? {});
    if (datasetIds.length === 0) return "viz-data-empty";
    // We currently render one dataset per viz. If that changes, join keeps the key stable.
    return `viz-data-${datasetIds.sort().join(",")}`;
  }, [data]);
  const normalizedTree = useMemo(
    () => (tree ? normalizeVizTreeChildren(tree) : null),
    [tree],
  );
  const initialState = useMemo(
    () => ({
      ...(normalizedTree?.state ?? {}),
      ...(data ?? {}),
    }),
    [data, normalizedTree?.state],
  );

  if (!normalizedTree) return null;

  return (
    <StateProvider key={dataKey} initialState={initialState}>
      <VisibilityProvider>
        <ActionProvider handlers={EMPTY_ACTION_HANDLERS}>
          <Renderer
            spec={normalizedTree}
            registry={vizComponentRegistry}
            loading={isStreaming}
          />
        </ActionProvider>
      </VisibilityProvider>
    </StateProvider>
  );
});
